/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

/*
* When adding modules to the namespace, ensure that they are added in alphabetical order according to module name.
*/

// MODULES //

import setReadOnly from '@stdlib/utils/define-read-only-property';


// MAIN //

/**
* Top-level namespace.
*
* @namespace normal
*/
var normal = {};

/**
* @name cdf
* @memberof normal
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/normal/cdf}
*/
import cdf from './../../../../base/dists/normal/cdf';
setReadOnly( normal, 'cdf', cdf );

/**
* @name Normal
* @memberof normal
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/normal/ctor}
*/
import Normal from './../../../../base/dists/normal/ctor';
setReadOnly( normal, 'Normal', Normal );

/**
* @name entropy
* @memberof normal
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/normal/entropy}
*/
import entropy from './../../../../base/dists/normal/entropy';
setReadOnly( normal, 'entropy', entropy );

/**
* @name kurtosis
* @memberof normal
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/normal/kurtosis}
*/
import kurtosis from './../../../../base/dists/normal/kurtosis';
setReadOnly( normal, 'kurtosis', kurtosis );

/**
* @name logcdf
* @memberof normal
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/normal/logcdf}
*/
import logcdf from './../../../../base/dists/normal/logcdf';
setReadOnly( normal, 'logcdf', logcdf );

/**
* @name logpdf
* @memberof normal
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/normal/logpdf}
*/
import logpdf from './../../../../base/dists/normal/logpdf';
setReadOnly( normal, 'logpdf', logpdf );

/**
* @name mean
* @memberof normal
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/normal/mean}
*/
import mean from './../../../../base/dists/normal/mean';
setReadOnly( normal, 'mean', mean );

/**
* @name median
* @memberof normal
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/normal/median}
*/
import median from './../../../../base/dists/normal/median';
setReadOnly( normal, 'median', median );

/**
* @name mgf
* @memberof normal
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/normal/mgf}
*/
import mgf from './../../../../base/dists/normal/mgf';
setReadOnly( normal, 'mgf', mgf );

/**
* @name mode
* @memberof normal
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/normal/mode}
*/
import mode from './../../../../base/dists/normal/mode';
setReadOnly( normal, 'mode', mode );

/**
* @name pdf
* @memberof normal
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/normal/pdf}
*/
import pdf from './../../../../base/dists/normal/pdf';
setReadOnly( normal, 'pdf', pdf );

/**
* @name quantile
* @memberof normal
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/normal/quantile}
*/
import quantile from './../../../../base/dists/normal/quantile';
setReadOnly( normal, 'quantile', quantile );

/**
* @name skewness
* @memberof normal
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/normal/skewness}
*/
import skewness from './../../../../base/dists/normal/skewness';
setReadOnly( normal, 'skewness', skewness );

/**
* @name stdev
* @memberof normal
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/normal/stdev}
*/
import stdev from './../../../../base/dists/normal/stdev';
setReadOnly( normal, 'stdev', stdev );

/**
* @name variance
* @memberof normal
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/normal/variance}
*/
import variance from './../../../../base/dists/normal/variance';
setReadOnly( normal, 'variance', variance );


// EXPORTS //

export default normal;
